
































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { Audit, AuditOperationType } from '@/models'
import ApiClient from '@/services/api-client'
import AccountAuditDetailModal from '@/components/modals/AccountAuditDetailModal.vue'
import { Utils } from '@/utils'

@Component({
  components: {
    AccountAuditDetailModal
  }
})
export default class AccountAudit extends Vue {
  @Prop({ required: true }) accountId!: string

  private audits: Audit[] = []
  private totalResults = 0
  private loading = false
  private auditsCurrentPage = 1
  private auditsPerPage = 10
  private filterType = 'all'
  private filterOperation = 'all'
  private filterUser = ''
  private operationTypes: AuditOperationType[] = []
  private delayedLoadAudits = Utils.delay(this.loadAudits, 500)

  private auditFields = [
    {
      key: 'createdOn',
      label: this.$t('account_detail.audit.columns.date'),
      sortable: true
    },
    {
      key: 'modelKt',
      label: this.$t('account_detail.audit.columns.type'),
      sortable: true
    },
    {
      key: 'operationType',
      label: this.$t('account_detail.audit.columns.operation'),
      sortable: true
    },
    {
      key: 'userId',
      label: this.$t('account_detail.audit.columns.user'),
      sortable: true
    },
    {
      key: 'actions',
      label: this.$t('table.actions'),
      sortable: false,
      class: 'text-center'
    }
  ]

  private typeOptions = [
    { value: 'all', text: this.$t('account_detail.audit.all_types') },
    { value: 'Account', text: this.$t('account_detail.audit.type_account') },
    { value: 'Permission', text: this.$t('account_detail.audit.type_permission') },
    { value: 'AccountActions', text: this.$t('account_detail.audit.type_actions') },
    { value: 'AccountManager', text: this.$t('account_detail.audit.type_manager') }
  ]

  get operationOptions() {
    const options = [{ value: 'all', text: this.$t('account_detail.audit.all_operations').toString() }]
    this.operationTypes.forEach(type => {
      options.push({
        value: type.id,
        text: this.getOperationLabel(type.id)
      })
    })
    return options
  }

  async mounted() {
    await this.loadOperationTypes()
  }

  @Watch('accountId', { immediate: true })
  async onAccountIdChange() {
    if (this.accountId) {
      await this.loadAudits()
    }
  }

  @Watch('filterType')
  async onFilterTypeChange() {
    if (this.auditsCurrentPage === 1) {
      await this.loadAudits()
    } else {
      this.auditsCurrentPage = 1
    }
  }

  @Watch('filterOperation')
  async onFilterOperationChange() {
    if (this.auditsCurrentPage === 1) {
      await this.loadAudits()
    } else {
      this.auditsCurrentPage = 1
    }
  }

  @Watch('filterUser')
  onFilterUserChange() {
    if (this.auditsCurrentPage === 1) {
      this.delayedLoadAudits.apply(this)
    } else {
      this.auditsCurrentPage = 1
    }
  }

  @Watch('auditsCurrentPage')
  async onPageChange() {
    await this.loadAudits()
  }

  async loadOperationTypes() {
    try {
      this.operationTypes = await ApiClient.getAuditOperationTypes()
    } catch (error) {
      console.error('Error loading operation types:', error)
    }
  }

  async loadAudits() {
    try {
      this.loading = true
      const offset = (this.auditsCurrentPage - 1) * this.auditsPerPage
      const result = await ApiClient.searchAccountAudit(
        this.accountId,
        offset,
        this.auditsPerPage,
        this.filterType,
        this.filterOperation,
        this.filterUser || null
      )
      this.audits = result.items
      this.totalResults = result.totalResults
    } catch (error) {
      console.error('Error loading audits:', error)
      this.$bvToast.toast(this.$t('account_detail.audit.messages.error_loading').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    } finally {
      this.loading = false
    }
  }

  getModelVariant(modelKt: string): string {
    const variants: { [key: string]: string } = {
      Account: 'info',
      Permission: 'primary',
      AccountActions: 'warning',
      AccountManager: 'success'
    }
    return variants[modelKt] || 'secondary'
  }

  getModelLabel(modelKt: string): string {
    const labels: { [key: string]: string } = {
      Account: this.$t('account_detail.audit.type_account').toString(),
      Permission: this.$t('account_detail.audit.type_permission').toString(),
      AccountActions: this.$t('account_detail.audit.type_actions').toString(),
      AccountManager: this.$t('account_detail.audit.type_manager').toString()
    }
    return labels[modelKt] || modelKt
  }

  getOperationVariant(operationType: string): string {
    const variants: { [key: string]: string } = {
      INSERT: 'success',
      UPDATE: 'info',
      DELETE: 'danger'
    }
    return variants[operationType] || 'secondary'
  }

  getOperationLabel(operationType: string): string {
    const labels: { [key: string]: string } = {
      INSERT: this.$t('account_detail.audit.operation_insert').toString(),
      UPDATE: this.$t('account_detail.audit.operation_update').toString(),
      DELETE: this.$t('account_detail.audit.operation_delete').toString()
    }
    return labels[operationType] || operationType
  }

  clearFilters() {
    this.filterType = 'all'
    this.filterOperation = 'all'
    this.filterUser = ''
  }

  showDetail(audit: Audit) {
    const modal = this.$refs.auditDetailModal as InstanceType<typeof AccountAuditDetailModal>
    if (modal) {
      modal.show(audit)
    }
  }
}
